import * as React from 'react';
import * as _ from 'lodash';
import {
  Logging,
  hashObject,
  Image,
  ControlState,
  ControlStyleClass,
  renderEdit,
  renderDate,
  renderSelect,
  Coverage,
  renderLabel,
  renderLinks,
  settingContextObjectLookup,
  capitalizeFirstLetterEachWord,
  settingCertificateCustomField,
  SettingsContextObject,
  ControlValidation,
  stringToDate,
  Certificate,
  formatDate,
  DateFormatEnum,
  getCurrentDateOnly,
} from '@vcd-ecm-ui/Common';
import {
  VtxCollapse,
  VtxCollapsePanel,
} from '@vertexinc/vtx-ui-react-component-library';
import '../../sidebyside-viewer.scss';
import SideBySideViewerCertificateTabProps from './sbsCertificateTabProps.interface';
import {
  getProducts,
  getReason,
  combineNameCode,
  getCertificateNumbers,
  getSourceOptions,
} from '../../../helpers/SideBySideViewer';

const controlStyle: ControlStyleClass = {
  fieldHeader: 'sbsFieldHeader',
  fieldBase: 'sbsFieldBase',
  fieldEnabled: 'sbsFieldEnabled',
  fieldDisabled: 'sbsFieldDisabled',
  fieldValidation: 'sbsFieldValidation',
};

const controlStyleLabel: ControlStyleClass = {
  fieldHeader: 'sbsFieldHeader',
  fieldBase: 'sbsFieldBase sbsFieldLabel',
  fieldEnabled: 'sbsFieldEnabled',
  fieldDisabled: 'sbsFieldDisabled',
};

const controlValidation: ControlValidation = {
  requiredFields: [],
  emailFields: [],
  phoneFields: [],
  phoneExtensionFields: [],
  postalCodeFields: [],
  customMessages: [],
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const controlStateEnabled: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const SideBySideViewerCertificateTab = ({
  certificate,
  editable,
  setEditedCertificate,
}: SideBySideViewerCertificateTabProps): JSX.Element => {
  const { settingsContext, setSettingsContext } = React.useContext(SettingsContextObject);
  const [activeJurisdictionKey, setActiveJurisdictionKey] = React.useState(null);

  // Get the User Context
  // Jurisdiction Panel Selected
  const onJurisdictionPanelSelected = (key: string): void => {
    Logging.Debug('Jurisdiction Panel Selected', key);

    // close the Collapse if same link is clicked again
    if (activeJurisdictionKey === key) {
      setActiveJurisdictionKey(null);
    } else {
      setActiveJurisdictionKey(key);
    }
  };

  // eslint-disable-next-line arrow-body-style
  const renderDateValue = (value: string): string => {
    return value || '--';
  };

  const {
    customer,
    seller,
    form,
    coverages,
  } = certificate;

  const multiJurisdiction = coverages && coverages.length > 1;
  const singleJurisdiction = !multiJurisdiction;
  // Get the First Coverage
  const singleCoverage: Coverage = coverages && coverages.length === 1 ? coverages[0] : null;

  const [customerName, setCustomerName] = React.useState(customer ? customer.name : null);
  const [customerCode, setCustomerCode] = React.useState(customer ? customer.code : null);
  const customerNameCode = combineNameCode(customerName, customerCode);
  const [certificateClassName, setCertificateClassName] = React.useState(certificate.className);
  const [jurisdiction, setJurisdiction] = React.useState(singleCoverage && singleCoverage.jurisdiction ? singleCoverage.jurisdiction.name : null);
  const [reason, setReason] = React.useState(getReason(certificate));
  const [certificateScope, setCertificateScope] = React.useState(capitalizeFirstLetterEachWord(certificate.certificateScope));
  const [startDate, setStartDate] = React.useState(stringToDate(certificate.effectiveDate));
  const [endDate, setEndDate] = React.useState(stringToDate(certificate.endDate));
  const [expirationDate, setExpirationDate] = React.useState(singleCoverage ? renderDateValue(singleCoverage.expirationDate) : null);
  const [issueDate, setIssueDate] = React.useState(singleCoverage ? renderDateValue(singleCoverage.issueDate) : null);
  const [reviewDate, setReviewDate] = React.useState(singleCoverage ? renderDateValue(singleCoverage.reviewDate) : null);
  const [sellerName, setSellerName] = React.useState(seller ? seller.name : null);
  const [sellerCode, setSellerCode] = React.useState(seller ? seller.code : null);
  const sellerNamesCode = combineNameCode(sellerName, sellerCode);
  const [source, setSource] = React.useState(certificate.source);
  const [formName, setFormName] = React.useState(form ? form.name : null);
  const [products, setProducts] = React.useState(getProducts(certificate));
  const [certificateNumber, setCertificateNumber] = React.useState(getCertificateNumbers(certificate));
  const [notes, setNotes] = React.useState(certificate.notes);
  const [customField1, setCustomField1] = React.useState(certificate.customField1);
  const [customField2, setCustomField2] = React.useState(certificate.customField2);
  const [customField3, setCustomField3] = React.useState(certificate.customField3);
  const [customField4, setCustomField4] = React.useState(certificate.customField4);
  const [customField5, setCustomField5] = React.useState(certificate.customField5);
  const [customField6, setCustomField6] = React.useState(certificate.customField6);
  const [customField7, setCustomField7] = React.useState(certificate.customField7);
  const [customField8, setCustomField8] = React.useState(certificate.customField8);
  const [customField9, setCustomField9] = React.useState(certificate.customField9);
  const [customField10, setCustomField10] = React.useState(certificate.customField10);

  // Render Coverage
  const renderCoverage = (coverage: Coverage): JSX.Element => {
    Logging.Debug('Render Coverage:', coverage);

    return (
      <VtxCollapsePanel header={coverage.jurisdiction.name} key={coverage.jurisdiction.name} showArrow className="jurisdictionsCollapsePanel">
        <div className="jurisdictionsCollapsePanelContent">
          {renderEdit(33, 'issue-date', 'ISSUE DATE', 'text', null, renderDateValue(coverage.issueDate), null, controlStateDisabled, controlStyle)}
          {renderEdit(33, 'expiration-dates', 'EXPIRATION DATE', 'text', null,
            renderDateValue(coverage.expirationDate), null, controlStateDisabled, controlStyle)}
          {renderEdit(33, 'review-date', 'REVIEW DATE', 'text', null, renderDateValue(coverage.reviewDate), null, controlStateDisabled, controlStyle)}
        </div>
      </VtxCollapsePanel>
    );
  };

  // Render Coverages
  const renderCoverages = (): JSX.Element[] => {
    Logging.Debug('Render Coverages:', coverages.length);

    // eslint-disable-next-line arrow-body-style
    return coverages.map((coverage: Coverage) => {
      return renderCoverage(coverage);
    });
  };

  // Render Coverage Links
  const renderCoverageLink = (coverage: Coverage, lastCoverage: boolean): JSX.Element => {
    Logging.Debug('Render Coverage Link:', coverage);

    return (
      <div key={coverage.jurisdiction.id}>
        {/* eslint-disable-next-line react/button-has-type */}
        <button
          className="coverageLink"
          onClick={() => onJurisdictionPanelSelected(coverage.jurisdiction.name)}
        >
          {coverage.jurisdiction.name}
          {
            !lastCoverage
            && (
              <>
                ,
              </>
            )
          }
        </button>
        {
          !lastCoverage
          && (
            <>
              &nbsp;
            </>
          )
        }
      </div>
    );
  };

  // Validate the Edited Certificate
  const validateCertificate = (): boolean => {
    if (startDate && endDate) {
      if (endDate < startDate) {
        return false;
      }
    }

    if (endDate) {
      const currentDate = getCurrentDateOnly();

      if (endDate < currentDate) {
        return false;
      }
    }

    return true;
  };

  // Check the Certificate for Changes
  const checkCertificateChanges = (): boolean => {
    if (stringToDate(certificate.endDate) !== endDate) {
      return true;
    }

    return false;
  };

  // End Date was modified during an Edit
  React.useEffect(() => {
    if (editable) {
      // Validate the Certificate Values and check for Changes
      if (validateCertificate() && checkCertificateChanges()) {
        // Create the Edited Certificate
        const editedCertificate: Certificate = {
          uuid: certificate.uuid,
          endDate: formatDate(endDate, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }),
        };

        Logging.Info('Set Edit Certificate:', editedCertificate);

        // Set the Edit Certificate
        setEditedCertificate(editedCertificate);
      } else {
        Logging.Info('Clear Edit Certificate');
        // Clear the Edit Certificate
        setEditedCertificate(null);
      }
    }
  }, [endDate]);

  // Render Coverage Links
  const renderCoverageLinks = (): JSX.Element[] => {
    Logging.Debug('Render Coverage Links:', coverages.length);
    let index = 0;

    // eslint-disable-next-line arrow-body-style
    return coverages.map((coverage: Coverage) => {
      index += 1;
      return renderCoverageLink(coverage, index === coverages.length);
    });
  };

  const customFieldHeader1: string = settingContextObjectLookup(settingsContext.certificates, 1, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader2: string = settingContextObjectLookup(settingsContext.certificates, 2, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader3: string = settingContextObjectLookup(settingsContext.certificates, 3, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader4: string = settingContextObjectLookup(settingsContext.certificates, 4, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader5: string = settingContextObjectLookup(settingsContext.certificates, 5, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader6: string = settingContextObjectLookup(settingsContext.certificates, 6, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader7: string = settingContextObjectLookup(settingsContext.certificates, 7, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader8: string = settingContextObjectLookup(settingsContext.certificates, 8, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader9: string = settingContextObjectLookup(settingsContext.certificates, 9, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader10: string = settingContextObjectLookup(settingsContext.certificates, 10, settingCertificateCustomField, 'Custom Field');

  if (editable) {
    controlValidation.customMessages = [];

    if (startDate && endDate) {
      if (endDate < startDate) {
        controlValidation.customMessages = [...controlValidation.customMessages,
          { field: 'end-date', message: 'End Date cannot be before Start Date', type: 'Error' }];
      }
    }

    if (endDate) {
      const currentDate = getCurrentDateOnly();

      if (endDate < currentDate) {
        controlValidation.customMessages = [...controlValidation.customMessages,
          { field: 'end-date', message: 'End Date cannot be before Current Date', type: 'Error' }];
      }
    }
  }

  return (
    <>
      <div id="certificate-information-tab" className="sbsContentLeftTabContent">
        {
          editable
            ? (
              <>
                {renderEdit(33, 'customer-name', 'CUSTOMER NAME', 'text', null, customerName, setCustomerName, controlStateDisabled, controlStyle)}
                {renderEdit(33, 'customer-code', 'CUSTOMER CODE', 'text', null, customerCode, setCustomerCode, controlStateDisabled, controlStyle)}
              </>
            )
            : (
              <>
                {renderEdit(66, 'customer-name-code', 'CUSTOMER', 'text', null, customerNameCode, null, controlStateDisabled, controlStyle)}
              </>
            )
        }
        {renderSelect(33, 'type', 'TYPE', null, false, true, null, false, null,
          certificateClassName, setCertificateClassName, controlStateDisabled, controlStyle)}
        {
          singleJurisdiction
          && (
            <>
              {renderEdit(100, 'jurisdictions', 'JURISDICTION', 'text', null, jurisdiction, setJurisdiction, controlStateDisabled, controlStyle)}
            </>
          )
        }
        {
          multiJurisdiction
          && (
            <>
              {renderLinks(100, 'jurisdictions', 'JURISDICTIONS', renderCoverageLinks(), controlStateDisabled, controlStyleLabel)}
            </>
          )
        }
        {renderEdit(66, 'reason', 'REASON', 'text', null, reason, setReason, controlStateDisabled, controlStyle)}
        {renderSelect(33, 'scope', 'SCOPE', null, false, true, null, false, null,
          certificateScope, setCertificateScope, controlStateDisabled, controlStyle)}
        {renderDate(33, 'start-date', 'START DATE', null, startDate, setStartDate, null, controlStateDisabled, controlStyle)}
        {renderDate(33, 'end-date', 'END DATE', null, endDate, setEndDate, null, editable ? controlStateEnabled : controlStateDisabled,
          controlStyle, controlValidation)}
        <div className="field33" />
        {
          singleJurisdiction
          && (
            <>
              {renderEdit(33, 'issue-date', 'ISSUE DATE', 'text', null, issueDate, setIssueDate, controlStateDisabled, controlStyle)}
              {renderEdit(33, 'expiration-date', 'EXPIRATION DATE', 'text', null,
                expirationDate, setExpirationDate, controlStateDisabled, controlStyle)}
              {renderEdit(33, 'review-date', 'REVIEW DATE', 'text', null, reviewDate, setReviewDate, controlStateDisabled, controlStyle)}
            </>
          )
        }
        {
          multiJurisdiction
          && (
            <>
              {renderLabel(100, 'jurisdiction-dates', 'JURISDICTION SPECIFIC DATES', controlStateDisabled, controlStyleLabel)}
              <div className="jurisdictions">
                <VtxCollapse
                  accordion
                  onChange={onJurisdictionPanelSelected}
                  className="jurisdictionsCollapse"
                  activeKey={activeJurisdictionKey}
                >
                  {renderCoverages()}
                </VtxCollapse>
              </div>
            </>
          )
        }
        {
          editable
            ? (
              <>
                {renderEdit(33, 'seller-name', 'SELLER NAME', 'text', null, sellerName, setSellerName, controlStateDisabled, controlStyle)}
                {renderEdit(33, 'seller-code', 'SELLER CODE', 'text', null, sellerCode, setSellerCode, controlStateDisabled, controlStyle)}
              </>
            )
            : (
              <>
                {renderEdit(66, 'seller', 'SELLER', 'text', null, sellerNamesCode, null, controlStateDisabled, controlStyle)}
              </>
            )
        }
        {renderSelect(33, 'source', 'SOURCE', null, false, true, getSourceOptions(), false, null,
          source, setSource, controlStateDisabled, controlStyle)}
        {renderEdit(100, 'form', 'FORM', 'text', null, formName, setFormName, controlStateDisabled, controlStyle)}
        {renderEdit(100, 'products', 'PRODUCTS', 'text', null, products, setProducts, controlStateDisabled, controlStyle)}
        {renderEdit(100, 'certificate-number', 'CERTIFICATE NUMBER(S)', 'text', null,
          certificateNumber, setCertificateNumber, controlStateDisabled, controlStyle)}
        {renderEdit(100, 'notes', 'NOTES', 'text', null, notes, setNotes, controlStateDisabled, controlStyle)}
        {
          customFieldHeader1
          && (
            renderEdit(50, 'customfield-1', customFieldHeader1, 'text', null, customField1, setCustomField1, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader2
          && (
            renderEdit(50, 'customfield-2', customFieldHeader2, 'text', null, customField2, setCustomField2, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader3
          && (
            renderEdit(50, 'customfield-3', customFieldHeader3, 'text', null, customField3, setCustomField3, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader4
          && (
            renderEdit(50, 'customfield-4', customFieldHeader4, 'text', null, customField4, setCustomField4, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader5
          && (
            renderEdit(50, 'customfield-5', customFieldHeader5, 'text', null, customField5, setCustomField5, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader6
          && (
            renderEdit(50, 'customfield-6', customFieldHeader6, 'text', null, customField6, setCustomField6, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader7
          && (
            renderEdit(50, 'customfield-7', customFieldHeader7, 'text', null, customField7, setCustomField7, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader8
          && (
            renderEdit(50, 'customfield-8', customFieldHeader8, 'text', null, customField8, setCustomField8, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader9
          && (
            renderEdit(50, 'customfield-9', customFieldHeader9, 'text', null, customField9, setCustomField9, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader10
          && (
            renderEdit(50, 'customfield-10', customFieldHeader10, 'text', null, customField10, setCustomField10, controlStateDisabled, controlStyle)
          )
        }
      </div>
    </>
  );
};

export default SideBySideViewerCertificateTab;
